import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { MagnifyingGlass, Buildings, CheckCircle, Prohibit, CurrencyGbp, X, CaretUp, CaretDown, PaperPlaneTilt, Trash, Gift, Key, ShieldStar, Warning, SlidersHorizontal, FloppyDisk, BookmarkSimple, Rows, ListBullets, Clock } from "@phosphor-icons/react";

const gbp = (n) => `£${Number(n || 0).toLocaleString("en-GB")}`;

function SetupRing({ setup }) {
  if (!setup) return <span className="text-navy/30 text-xs">—</span>;
  const pct = setup.percent || 0;
  const r = 13, C = 2 * Math.PI * r;
  const colour = pct === 100 ? "#00A859" : pct >= 50 ? "#F37A24" : "#64748B";
  const STEP_LABELS = { connected: "Connected", org_selected: "Org selected", codes_configured: "Codes tagged", has_schedules: "Imported" };
  const title = Object.keys(STEP_LABELS).map((k) => `${setup[k] ? "✓" : "○"} ${STEP_LABELS[k]}`).join("\n");
  return (
    <div className="flex items-center gap-2" title={title} data-testid="setup-ring">
      <svg width="34" height="34" viewBox="0 0 34 34" className="shrink-0">
        <circle cx="17" cy="17" r={r} fill="none" stroke="#eef0f5" strokeWidth="4" />
        <circle cx="17" cy="17" r={r} fill="none" stroke={colour} strokeWidth="4" strokeLinecap="round"
          strokeDasharray={C} strokeDashoffset={C * (1 - pct / 100)} transform="rotate(-90 17 17)" />
        <text x="17" y="21" textAnchor="middle" fontSize="9" fontWeight="700" fill={colour}>{pct}</text>
      </svg>
      <span className="text-[0.65rem] text-navy/55">{setup.done}/{setup.total} steps</span>
    </div>
  );
}

const ORG_RANGES = {
  All: () => true,
  "0": (n) => n === 0,
  "1": (n) => n === 1,
  "2–10": (n) => n >= 2 && n <= 10,
  "11–20": (n) => n >= 11 && n <= 20,
  "21–50": (n) => n >= 21 && n <= 50,
  "51+": (n) => n >= 51,
};

const SORT_ACCESSORS = {
  name: (a) => (a.name || "").toLowerCase(),
  plan: (a) => (a.plan_name || "").toLowerCase(),
  cycle: (a) => (a.status === "trialing" ? "" : (a.billing_cycle || "monthly")),
  value: (a) => Number(a.mrr || 0),
  orgs: (a) => Number(a.orgs || 0),
  status: (a) => (a.status || "").toLowerCase(),
  ltv: (a) => Number(a.revenue_to_date || 0),
};

// Columns the admin can show/hide (identity + Actions are always pinned & visible).
const HIDEABLE_COLUMNS = [
  { key: "plan", label: "Plan" },
  { key: "cycle", label: "Type" },
  { key: "value", label: "Value" },
  { key: "orgs", label: "Orgs" },
  { key: "status", label: "Status" },
  { key: "setup", label: "Setup" },
  { key: "onboarding", label: "Onboarding" },
  { key: "ltv", label: "Lifetime value" },
];

// One-tap built-in views (always available; map onto existing filters).
const BUILTIN_VIEWS = [
  { id: "__all", name: "All accounts", filters: { q: "", plan: "All", orgs: "All", status: "All", comp: "All" } },
  { id: "__comp", name: "Comp accounts", filters: { q: "", plan: "All", orgs: "All", status: "All", comp: "comp" } },
  { id: "__trialing", name: "Trialing", filters: { q: "", plan: "All", orgs: "All", status: "trialing", comp: "All" } },
  { id: "__active", name: "Active", filters: { q: "", plan: "All", orgs: "All", status: "active", comp: "All" } },
  { id: "__suspended", name: "Suspended", filters: { q: "", plan: "All", orgs: "All", status: "suspended", comp: "All" } },
];

const CYCLE_STYLE = {
  annual: "bg-orange/10 text-orange",
  monthly: "bg-slate-100 text-navy/70",
};

function SortTh({ label, sortKey, sort, onSort, align = "left", className = "" }) {
  const active = sort.key === sortKey;
  return (
    <th className={`px-3 py-3 font-semibold overline ${className}`}>
      <button onClick={() => onSort(sortKey)} data-testid={`accounts-sort-${sortKey}`}
        className={`inline-flex items-center gap-1 hover:text-navy transition-colors ${align === "right" ? "flex-row-reverse" : ""} ${active ? "text-navy" : ""}`}>
        {label}
        <span className="inline-flex flex-col -space-y-1.5">
          <CaretUp size={9} weight="fill" className={active && sort.dir === "asc" ? "text-[#F37A24]" : "text-slate-300"} />
          <CaretDown size={9} weight="fill" className={active && sort.dir === "desc" ? "text-[#F37A24]" : "text-slate-300"} />
        </span>
      </button>
    </th>
  );
}

const STATUS_STYLE = {
  active: "bg-positive/10 text-positive",
  trialing: "bg-orange/10 text-orange",
  suspended: "bg-warn/15 text-warn",
  cancelled: "bg-slate-200 text-navy/70",
};

const OB_STATE_LABEL = {
  not_connected: "Not connected",
  connected: "Xero connected",
  active: "Register live",
};

function StatCard({ label, value, color, active, onClick }) {
  return (
    <button onClick={onClick} data-testid={`stat-card-${label.toLowerCase()}`}
      className={`card-surface px-5 py-4 text-left transition-all ${active ? "ring-2 ring-[#F37A24]" : "hover:-translate-y-0.5"}`}>
      <div className="overline text-navy/65 mb-1">{label}</div>
      <div className="font-heading font-black text-3xl" style={{ color }}>{value}</div>
    </button>
  );
}

const inCls2 = "bg-white border border-slate-200 rounded-xl px-3 py-2 text-sm text-navy focus:border-[#F37A24] focus:ring-2 focus:ring-[#F37A24]/20 outline-none";

function PlatformTools({ admins, onGrant, onRevoke, busy }) {
  const [email, setEmail] = useState("");
  return (
    <div className="card-surface p-5 mb-6" data-testid="platform-access-panel">
      <div className="flex items-center gap-2 mb-1">
        <ShieldStar size={18} weight="fill" className="text-[#F37A24]" />
        <h2 className="font-semibold text-navy">Back-office access</h2>
      </div>
      <p className="text-navy/65 text-xs mb-3">Grant an existing user access to this console. They keep their own account and login — this only adds platform-admin rights.</p>
      <div className="flex flex-wrap gap-2 mb-3">
        <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="user@practice.co.uk"
          className={`${inCls2} flex-1 min-w-[220px]`} data-testid="platform-grant-email" />
        <button onClick={() => { onGrant(email.trim().toLowerCase()); setEmail(""); }} disabled={busy || !email.trim()}
          data-testid="platform-grant-btn"
          className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-xl text-white disabled:opacity-50" style={{ background: "#0E0F3F" }}>
          <Key size={15} weight="fill" /> Grant access
        </button>
      </div>
      <div className="flex flex-wrap gap-2">
        {(admins || []).map((a) => (
          <span key={a.id} data-testid={`platform-admin-${a.email}`}
            className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-full bg-slate-100 text-navy">
            <ShieldStar size={12} weight="fill" className="text-[#F37A24]" />
            {a.email}
            {a.is_seeded_superadmin
              ? <span className="text-[0.6rem] text-navy/45 uppercase">owner</span>
              : <button onClick={() => onRevoke(a.email)} disabled={busy} title="Revoke access"
                  data-testid={`platform-revoke-${a.email}`} className="text-navy/40 hover:text-[#E5484D]"><X size={12} weight="bold" /></button>}
          </span>
        ))}
      </div>
    </div>
  );
}

function TransferModal({ account, onClose, onConfirm, busy }) {
  const [email, setEmail] = useState("");
  return (
    <div className="fixed inset-0 bg-navy/40 backdrop-blur-sm z-50 flex items-center justify-center px-6" data-testid="transfer-modal">
      <div className="bg-white rounded-2xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading font-black text-navy text-xl">Reassign account</h3>
          <button onClick={onClose} data-testid="transfer-modal-close" className="text-navy/65 hover:text-navy"><X size={20} /></button>
        </div>
        <p className="text-navy/65 text-xs mb-3">Hand <b>{account.name}</b> to another user. They become the owner and a member of this account. They must already have a WorkWithLottie login.</p>
        <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="new-owner@practice.co.uk"
          className={`${inCls2} w-full mb-3`} data-testid="transfer-email" />
        <button onClick={() => onConfirm(email.trim().toLowerCase())} disabled={busy || !email.trim()} data-testid="transfer-confirm-btn"
          className="w-full inline-flex items-center justify-center gap-2 text-sm font-bold px-4 py-2.5 rounded-xl text-white disabled:opacity-50" style={{ background: "#0E0F3F" }}>
          <PaperPlaneTilt size={15} weight="fill" /> {busy ? "Transferring…" : "Transfer ownership"}
        </button>
      </div>
    </div>
  );
}

function ExtendTrialModal({ account, onClose, onConfirm, busy }) {
  const [days, setDays] = useState(14);
  const currentEnd = account.trial_ends_at ? new Date(account.trial_ends_at) : null;
  const newEnd = () => {
    const base = currentEnd && currentEnd > new Date() ? currentEnd : new Date();
    const d = new Date(base); d.setDate(d.getDate() + (Number(days) || 0));
    return d.toLocaleDateString("en-GB");
  };
  return (
    <div className="fixed inset-0 bg-navy/40 backdrop-blur-sm z-50 flex items-center justify-center px-6" data-testid="extend-trial-modal">
      <div className="bg-white rounded-2xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2"><Clock size={20} weight="bold" style={{ color: "#F37A24" }} />
            <h3 className="font-heading font-black text-navy text-xl">Extend trial</h3></div>
          <button onClick={onClose} data-testid="extend-trial-close" className="text-navy/65 hover:text-navy"><X size={20} /></button>
        </div>
        <p className="text-navy/65 text-xs mb-3">
          Extend the trial for <b>{account.name}</b>{currentEnd ? <> — currently ends <b>{currentEnd.toLocaleDateString("en-GB")}</b></> : ""}.
          The extension is added from the later of today or the current end date.
        </p>
        <div className="flex flex-wrap gap-2 mb-3">
          {[7, 14, 30, 60].map((d) => (
            <button key={d} onClick={() => setDays(d)} data-testid={`extend-days-${d}`}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${Number(days) === d ? "border-[#F37A24] bg-[#F37A24]/10 text-[#F37A24]" : "border-slate-200 text-navy/70 hover:border-navy"}`}>+{d} days</button>
          ))}
        </div>
        <label className="block text-xs text-navy/70 mb-1.5">Or enter a number of days</label>
        <input type="number" min="1" max="730" value={days} onChange={(e) => setDays(e.target.value)}
          className={`${inCls2} w-full mb-2`} data-testid="extend-days-input" />
        <p className="text-xs text-navy/60 mb-4">New trial end date: <b className="text-navy">{newEnd()}</b></p>
        <button onClick={() => onConfirm(days)} disabled={busy || !Number(days)} data-testid="extend-trial-confirm"
          className="w-full inline-flex items-center justify-center gap-2 text-sm font-bold px-4 py-2.5 rounded-xl text-white disabled:opacity-50" style={{ background: "#F37A24" }}>
          <Clock size={15} weight="bold" /> {busy ? "Extending…" : `Extend by ${Number(days) || 0} day(s)`}
        </button>
      </div>
    </div>
  );
}


const COMP_REASONS = ["Internal", "Partner", "Beta", "Goodwill", "Other"];

function CompReasonModal({ count, onClose, onConfirm, busy }) {
  const [reason, setReason] = useState("Internal");
  const [note, setNote] = useState("");
  const [until, setUntil] = useState("");
  const final = reason === "Other" ? (note.trim() || "Other") : (note.trim() ? `${reason} — ${note.trim()}` : reason);
  return (
    <div className="fixed inset-0 bg-navy/40 backdrop-blur-sm z-50 flex items-center justify-center px-6" data-testid="comp-reason-modal">
      <div className="bg-white rounded-2xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2"><Gift size={20} weight="fill" className="text-positive" />
            <h3 className="font-heading font-black text-navy text-xl">Make comp{count > 1 ? ` (${count})` : ""}</h3></div>
          <button onClick={onClose} data-testid="comp-reason-close" className="text-navy/65 hover:text-navy"><X size={20} /></button>
        </div>
        <p className="text-navy/65 text-xs mb-3">Comp accounts stay fully active but are never charged — any Stripe subscription is cancelled. Record why:</p>
        <div className="flex flex-wrap gap-2 mb-3">
          {COMP_REASONS.map((r) => (
            <button key={r} onClick={() => setReason(r)} data-testid={`comp-reason-${r.toLowerCase()}`}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${reason === r ? "border-positive bg-positive/10 text-positive" : "border-slate-200 text-navy/70 hover:border-navy"}`}>{r}</button>
          ))}
        </div>
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional note (e.g. CASS internal)"
          className={`${inCls2} w-full mb-3`} data-testid="comp-reason-note" />
        <label className="block text-xs text-navy/70 mb-1.5">Auto-revert to chargeable on <span className="text-navy/40">(optional)</span></label>
        <input type="date" value={until} onChange={(e) => setUntil(e.target.value)}
          className={`${inCls2} w-full mb-4`} data-testid="comp-reason-until" />
        <button onClick={() => onConfirm(final, until)} disabled={busy} data-testid="comp-reason-confirm"
          className="w-full inline-flex items-center justify-center gap-2 text-sm font-bold px-4 py-2.5 rounded-xl text-white disabled:opacity-50" style={{ background: "#00A859" }}>
          <Gift size={15} weight="fill" /> {busy ? "Applying…" : `Mark comp`}
        </button>
      </div>
    </div>
  );
}

function SaveViewModal({ onClose, onSave }) {
  const [name, setName] = useState("");
  return (
    <div className="fixed inset-0 bg-navy/40 backdrop-blur-sm z-50 flex items-center justify-center px-6" data-testid="save-view-modal">
      <div className="bg-white rounded-2xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2"><BookmarkSimple size={20} weight="fill" className="text-[#F37A24]" />
            <h3 className="font-heading font-black text-navy text-xl">Save this view</h3></div>
          <button onClick={onClose} data-testid="save-view-close" className="text-navy/65 hover:text-navy"><X size={20} /></button>
        </div>
        <p className="text-navy/65 text-xs mb-3">Stores the current search, plan, orgs, status and billing filters as a one-tap view — saved to your admin profile.</p>
        <input value={name} onChange={(e) => setName(e.target.value)} autoFocus
          onKeyDown={(e) => { if (e.key === "Enter") onSave(name); }}
          placeholder="View name (e.g. Enterprise trials)"
          className={`${inCls2} w-full mb-4`} data-testid="save-view-name" />
        <button onClick={() => onSave(name)} disabled={!name.trim()} data-testid="save-view-confirm"
          className="w-full inline-flex items-center justify-center gap-2 text-sm font-bold px-4 py-2.5 rounded-xl text-white disabled:opacity-50" style={{ background: "#0E0F3F" }}>
          <FloppyDisk size={15} weight="bold" /> Save view
        </button>
      </div>
    </div>
  );
}


function DeletionLogModal({ entries, onClose, onRestore }) {
  const fmt = (s) => (s || "").slice(0, 16).replace("T", " ");
  const meta = {
    delete: { label: "Deleted", color: "#E5484D", icon: Trash },
    restore: { label: "Restored", color: "#00A859", icon: CheckCircle },
    transfer: { label: "Transferred", color: "#0E0F3F", icon: PaperPlaneTilt },
  };
  return (
    <div className="fixed inset-0 bg-navy/40 backdrop-blur-sm z-50 flex items-center justify-center px-6" data-testid="deletion-log-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg p-6 max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading font-black text-navy text-xl">Account activity log</h3>
          <button onClick={onClose} data-testid="deletion-log-close" className="text-navy/65 hover:text-navy"><X size={20} /></button>
        </div>
        {(!entries || entries.length === 0) ? (
          <p className="text-sm text-navy/60 text-center py-6">No account activity recorded yet.</p>
        ) : (
          <div className="space-y-2">
            {entries.map((e, i) => {
              const m = meta[e.type] || meta.delete; const Icon = m.icon;
              return (
                <div key={i} data-testid={`log-entry-${i}`} className="border border-slate-100 rounded-lg px-3 py-2.5">
                  <div className="flex items-center justify-between">
                    <span className="inline-flex items-center gap-1.5 font-semibold text-navy text-sm">
                      <Icon size={14} weight="fill" style={{ color: m.color }} /> {e.account_name || "—"}
                    </span>
                    <span className="text-[0.65rem] text-navy/50">{fmt(e.at)}</span>
                  </div>
                  <div className="text-xs text-navy/60 mt-0.5">
                    {e.type === "transfer"
                      ? <>{m.label}: {e.from_email || "—"} → <b>{e.to_email}</b></>
                      : e.type === "restore"
                        ? <>{m.label} by {e.by}</>
                        : <>{m.label} by {e.by} · {e.purged ? "purged" : e.restorable ? `restorable until ${fmt(e.restorable_until)}` : "restore window passed"}</>}
                  </div>
                  {e.type === "delete" && e.restorable && (
                    <button onClick={() => onRestore(e.account_id, e.account_name)} data-testid={`restore-${e.account_id}`}
                      className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border border-positive/40 text-positive bg-positive/5 hover:bg-positive/10">
                      <CheckCircle size={13} weight="fill" /> Undo (restore)
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function CleanupModal({ items, onClose, onConfirm, deleting }) {
  const [confirmText, setConfirmText] = useState("");
  const deletable = items.filter((p) => !p.protected);
  const protectedItems = items.filter((p) => p.protected);
  return (
    <div className="fixed inset-0 bg-navy/40 backdrop-blur-sm z-50 flex items-center justify-center px-6" data-testid="cleanup-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg p-6 max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Trash size={20} weight="fill" className="text-[#E5484D]" />
            <h3 className="font-heading font-black text-navy text-xl">Delete test accounts</h3>
          </div>
          <button onClick={onClose} data-testid="cleanup-modal-close" className="text-navy/65 hover:text-navy"><X size={20} /></button>
        </div>
        <div className="rounded-xl bg-[#F37A24]/[0.08] border border-[#F37A24]/25 p-3 mb-4">
          <p className="text-xs text-navy/75 leading-relaxed">
            The accounts below will be <b>soft-deleted</b>: their users lose access and any Stripe subscription is cancelled, but records are kept for <b>30 days</b> so you can undo from the activity log before they're purged for good.
          </p>
        </div>
        {deletable.length > 0 && (
          <div className="mb-4">
            <div className="overline text-navy/65 mb-2">Will be deleted ({deletable.length})</div>
            <div className="space-y-1.5">
              {deletable.map((p) => (
                <div key={p.id} data-testid={`cleanup-del-${p.id}`} className="flex items-center justify-between text-sm border border-slate-100 rounded-lg px-3 py-2">
                  <div><span className="font-semibold text-navy">{p.name || "—"}</span> <span className="text-navy/55 text-xs">{p.owner_email}</span></div>
                  <div className="text-[0.65rem] text-navy/55">{p.member_count} user{p.member_count === 1 ? "" : "s"} · {p.related_records} records{p.will_cancel_stripe ? " · Stripe" : ""}</div>
                </div>
              ))}
            </div>
          </div>
        )}
        {protectedItems.length > 0 && (
          <div className="mb-4">
            <div className="overline text-navy/65 mb-2">Protected — will be skipped ({protectedItems.length})</div>
            <div className="space-y-1.5">
              {protectedItems.map((p) => (
                <div key={p.id} data-testid={`cleanup-skip-${p.id}`} className="flex items-center justify-between text-sm bg-slate-50 rounded-lg px-3 py-2">
                  <span className="font-medium text-navy/70">{p.name || "—"}</span>
                  <span className="inline-flex items-center gap-1 text-[0.65rem] text-navy/55"><ShieldStar size={11} weight="fill" className="text-positive" /> {p.protected_reason}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {deletable.length > 0 ? (
          <>
            <label className="block text-xs text-navy/70 mb-1.5">Type <b>DELETE</b> to confirm</label>
            <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="DELETE"
              className={`${inCls2} w-full mb-3`} data-testid="cleanup-confirm-input" />
            <button onClick={onConfirm} disabled={deleting || confirmText !== "DELETE"} data-testid="cleanup-confirm-btn"
              className="w-full inline-flex items-center justify-center gap-2 text-sm font-bold px-4 py-2.5 rounded-xl text-white disabled:opacity-50" style={{ background: "#E5484D" }}>
              <Trash size={15} weight="fill" /> {deleting ? "Deleting…" : `Delete ${deletable.length} account${deletable.length === 1 ? "" : "s"}`}
            </button>
          </>
        ) : (
          <p className="text-sm text-navy/60 text-center py-2">Nothing to delete — all selected accounts are protected.</p>
        )}
      </div>
    </div>
  );
}

function PricingModal({ account, plans, onClose, onSaved }) {
  const [monthly, setMonthly] = useState(account.custom_enterprise?.monthly || "");
  const [annual, setAnnual] = useState(account.custom_enterprise?.annual || "");
  const [companies, setCompanies] = useState(account.custom_enterprise?.companies || "");
  const [saving, setSaving] = useState(false);
  const [stdPlan, setStdPlan] = useState("");
  const inCls = "w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-sm text-navy focus:border-[#F37A24] focus:ring-2 focus:ring-[#F37A24]/20 outline-none";

  const save = async () => {
    if (!monthly || Number(monthly) < 0) { toast.error("Enter a valid monthly price"); return; }
    setSaving(true);
    try {
      await api.put(`/admin/accounts/${account.id}/enterprise-pricing`, {
        monthly: Number(monthly),
        annual: annual ? Number(annual) : null,
        companies: companies ? Number(companies) : null,
      });
      toast.success("Enterprise pricing saved");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save pricing");
    } finally { setSaving(false); }
  };

  const applyStandard = async () => {
    if (!stdPlan) return;
    setSaving(true);
    try {
      await api.put(`/admin/accounts/${account.id}/plan`, { plan_id: stdPlan });
      toast.success("Plan updated");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update plan");
    } finally { setSaving(false); }
  };

  const stdPlans = (plans || []).filter((p) => !p.enterprise);

  return (
    <div className="fixed inset-0 bg-navy/40 backdrop-blur-sm z-50 flex items-center justify-center px-6" data-testid="enterprise-pricing-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
        <div className="flex items-start justify-between mb-1">
          <h3 className="font-heading font-bold text-navy text-lg flex items-center gap-2"><CurrencyGbp size={18} weight="bold" style={{ color: "#F37A24" }} /> Custom Enterprise pricing</h3>
          <button onClick={onClose} data-testid="pricing-modal-close" className="text-navy/65 hover:text-navy"><X size={20} /></button>
        </div>
        <p className="text-sm text-navy/70 mb-5">For <b>{account.name}</b> — sets the account to Enterprise with a bespoke (51+) price.</p>
        <div className="space-y-4">
          <label className="block"><span className="overline text-navy/70 block mb-1.5">Monthly price (£) *</span>
            <input type="number" min={0} className={inCls} value={monthly} onChange={(e) => setMonthly(e.target.value)} placeholder="750" data-testid="ent-monthly" /></label>
          <label className="block"><span className="overline text-navy/70 block mb-1.5">Annual price (£) — optional</span>
            <input type="number" min={0} className={inCls} value={annual} onChange={(e) => setAnnual(e.target.value)} placeholder="defaults to 10× monthly" data-testid="ent-annual" /></label>
          <label className="block"><span className="overline text-navy/70 block mb-1.5">Included companies — optional</span>
            <input type="number" min={51} className={inCls} value={companies} onChange={(e) => setCompanies(e.target.value)} placeholder="e.g. 80" data-testid="ent-companies" /></label>
        </div>
        <button onClick={save} disabled={saving} data-testid="ent-save"
          className="mt-6 w-full text-white font-semibold py-2.5 rounded-xl transition-transform hover:scale-[1.01] disabled:opacity-60" style={{ background: "#0E0F3F" }}>
          {saving ? "SAVING…" : "Save pricing"}
        </button>

        <div className="mt-5 pt-5 border-t border-slate-100">
          <span className="overline text-navy/70 block mb-2">Or set a standard plan</span>
          <div className="flex gap-2">
            <select className={inCls} value={stdPlan} onChange={(e) => setStdPlan(e.target.value)} data-testid="std-plan-select">
              <option value="">Choose a plan…</option>
              {stdPlans.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <button onClick={applyStandard} disabled={saving || !stdPlan} data-testid="std-plan-apply"
              className="shrink-0 px-4 rounded-xl border border-navy text-navy text-sm font-semibold hover:bg-navy hover:text-white transition-colors disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-navy">
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminConsole() {
  const [data, setData] = useState(null);
  const [plans, setPlans] = useState([]);
  const [q, setQ] = useState("");
  const [pricingFor, setPricingFor] = useState(null);
  const [busy, setBusy] = useState(null);
  const [obBusy, setObBusy] = useState(null);
  const [sort, setSort] = useState({ key: null, dir: "asc" });
  const [statusFilter, setStatusFilter] = useState("All");
  const [planFilter, setPlanFilter] = useState("All");
  const [orgsFilter, setOrgsFilter] = useState("All");
  const [selected, setSelected] = useState(new Set());
  const [platformAdmins, setPlatformAdmins] = useState([]);
  const [platformBusy, setPlatformBusy] = useState(false);
  const [compBusy, setCompBusy] = useState(null);
  const [cleanupItems, setCleanupItems] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [transferFor, setTransferFor] = useState(null);
  const [transferBusy, setTransferBusy] = useState(false);
  const [extendFor, setExtendFor] = useState(null);
  const [extendBusy, setExtendBusy] = useState(false);
  const [compModal, setCompModal] = useState(null); // { ids: [], bulk: bool }
  const [logOpen, setLogOpen] = useState(false);
  const [logEntries, setLogEntries] = useState([]);
  const [compFilter, setCompFilter] = useState("All");
  const [density, setDensity] = useState("comfortable");
  const [hiddenCols, setHiddenCols] = useState([]);
  const [savedViews, setSavedViews] = useState([]);
  const [colMenuOpen, setColMenuOpen] = useState(false);
  const [saveViewOpen, setSaveViewOpen] = useState(false);

  const show = (k) => !hiddenCols.includes(k);
  const persistPrefs = (patch) => {
    const body = { density, hidden_columns: hiddenCols, saved_views: savedViews, ...patch };
    api.put("/admin/table-prefs", body).catch(() => {});
  };
  const updateDensity = (d) => { setDensity(d); persistPrefs({ density: d }); };
  const toggleColumn = (key) => {
    const next = hiddenCols.includes(key) ? hiddenCols.filter((c) => c !== key) : [...hiddenCols, key];
    setHiddenCols(next); persistPrefs({ hidden_columns: next });
  };
  const applyView = (f) => {
    setQ(f.q || ""); load(f.q || "");
    setPlanFilter(f.plan || "All");
    setOrgsFilter(f.orgs || "All");
    setStatusFilter(f.status || "All");
    setCompFilter(f.comp || "All");
  };
  const saveCurrentView = (name) => {
    if (!name?.trim()) return;
    const view = { id: `v_${Date.now()}`, name: name.trim(), filters: { q, plan: planFilter, orgs: orgsFilter, status: statusFilter, comp: compFilter } };
    const next = [...savedViews, view];
    setSavedViews(next); persistPrefs({ saved_views: next });
    setSaveViewOpen(false);
    toast.success(`View "${view.name}" saved`);
  };
  const deleteView = (id) => {
    const next = savedViews.filter((v) => v.id !== id);
    setSavedViews(next); persistPrefs({ saved_views: next });
  };

  const doTransfer = async (email) => {
    if (!email || !transferFor) return;
    setTransferBusy(true);
    try {
      await api.post(`/admin/accounts/${transferFor.id}/transfer`, { email });
      toast.success(`${transferFor.name} reassigned to ${email}`);
      setTransferFor(null);
      load(q);
    } catch (e) { toast.error(e?.response?.data?.detail || "Transfer failed"); }
    finally { setTransferBusy(false); }
  };

  const doExtendTrial = async (days) => {
    if (!extendFor || !days) return;
    setExtendBusy(true);
    try {
      const { data } = await api.post(`/admin/accounts/${extendFor.id}/extend-trial`, { days: Number(days) });
      toast.success(`Trial extended — now ends ${new Date(data.trial_ends_at).toLocaleDateString("en-GB")}`);
      setExtendFor(null);
      load(q);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not extend trial"); }
    finally { setExtendBusy(false); }
  };

  const applyComp = async (reason, until) => {
    const ids = compModal.ids;
    setCompBusy("modal");
    try {
      if (ids.length > 1) {
        const { data } = await api.post("/admin/accounts/bulk-billing-exempt", { account_ids: ids, exempt: true, reason, until: until || "" });
        toast.success(`${data.updated} account${data.updated === 1 ? "" : "s"} marked comp`);
        setSelected(new Set());
      } else {
        await api.put(`/admin/accounts/${ids[0]}/billing-exempt`, { exempt: true, reason, until: until || "" });
        toast.success("Account marked comp — Stripe subscription cancelled where present");
      }
      setCompModal(null);
      load(q);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
    finally { setCompBusy(null); }
  };

  const restoreAccount = async (id, name) => {
    try {
      await api.post(`/admin/accounts/${id}/restore`);
      toast.success(`${name || "Account"} restored`);
      openLog();
      load(q);
    } catch (e) { toast.error(e?.response?.data?.detail || "Restore failed"); }
  };

  const openLog = async () => {
    try {
      const { data } = await api.get("/admin/deletion-log");
      setLogEntries(data.entries || []);
      setLogOpen(true);
    } catch (e) { toast.error("Could not load deletion log"); }
  };

  const loadPlatform = () => api.get("/admin/platform-admins")
    .then(({ data }) => setPlatformAdmins(data.admins || [])).catch(() => {});

  const grantPlatform = async (email) => {
    if (!email) return;
    setPlatformBusy(true);
    try {
      await api.post("/admin/platform-admins", { email, grant: true });
      toast.success(`Back-office access granted to ${email}`);
      loadPlatform();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not grant access"); }
    finally { setPlatformBusy(false); }
  };

  const revokePlatform = async (email) => {
    setPlatformBusy(true);
    try {
      await api.post("/admin/platform-admins", { email, grant: false });
      toast.success(`Access revoked for ${email}`);
      loadPlatform();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not revoke access"); }
    finally { setPlatformBusy(false); }
  };

  const toggleComp = async (a) => {
    if (!a.billing_exempt) { setCompModal({ ids: [a.id] }); return; }
    setCompBusy(a.id);
    try {
      await api.put(`/admin/accounts/${a.id}/billing-exempt`, { exempt: false });
      toast.success(`${a.name} is chargeable again`);
      load(q);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
    finally { setCompBusy(null); }
  };

  const toggleSelect = (id) => setSelected((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  const openCleanup = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    try {
      const { data } = await api.post("/admin/cleanup/preview", { account_ids: ids });
      setCleanupItems(data.plan || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not build cleanup preview"); }
  };

  const confirmCleanup = async () => {
    setDeleting(true);
    try {
      const { data } = await api.post("/admin/cleanup/delete", { account_ids: [...selected], confirm: true });
      toast.success(`Deleted ${data.deleted_count} account${data.deleted_count === 1 ? "" : "s"}${data.skipped_count ? ` · ${data.skipped_count} protected skipped` : ""}`);
      setSelected(new Set());
      setCleanupItems(null);
      load(q);
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    finally { setDeleting(false); }
  };


  const toggleSort = (key) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));

  const load = (query = "") => {
    api.get(`/admin/accounts${query ? `?q=${encodeURIComponent(query)}` : ""}`)
      .then(({ data }) => setData(data)).catch(() => toast.error("Could not load accounts"));
  };
  useEffect(() => {
    load(); loadPlatform();
    api.get("/billing/plans").then(({ data }) => setPlans(data.plans || [])).catch(() => {});
    api.get("/admin/table-prefs").then(({ data }) => {
      setDensity(data.density || "comfortable");
      setHiddenCols(data.hidden_columns || []);
      setSavedViews(data.saved_views || []);
    }).catch(() => {});
  }, []);

  const setStatus = async (acc, status) => {
    setBusy(acc.id);
    try {
      await api.put(`/admin/accounts/${acc.id}/status`, { status });
      toast.success({ suspended: "Account suspended", cancelled: "Account cancelled", active: "Account reactivated" }[status] || "Status updated");
      load(q);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update status");
    } finally { setBusy(null); }
  };

  const sendOnboarding = async (acc) => {
    setObBusy(acc.id);
    try {
      const { data } = await api.post(`/admin/accounts/${acc.id}/onboarding/send`, {});
      toast.success(`Sent "${data.label}" onboarding email to ${acc.owner_email}`);
      load(q);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send onboarding email");
    } finally { setObBusy(null); }
  };

  const counts = data?.counts || {};
  const allAccounts = data?.accounts || [];
  const planNames = [...new Set(allAccounts.map((a) => a.plan_name).filter(Boolean))].sort();
  const orgInRange = ORG_RANGES[orgsFilter] || (() => true);

  const filtered = allAccounts.filter((a) =>
    (statusFilter === "All" || a.status === statusFilter) &&
    (planFilter === "All" || a.plan_name === planFilter) &&
    (compFilter === "All" || (compFilter === "comp" ? a.billing_exempt : !a.billing_exempt)) &&
    orgInRange(Number(a.orgs || 0)));

  const acc = SORT_ACCESSORS[sort.key];
  const accounts = acc
    ? [...filtered].sort((x, y) => {
        const xv = acc(x), yv = acc(y);
        if (xv < yv) return sort.dir === "asc" ? -1 : 1;
        if (xv > yv) return sort.dir === "asc" ? 1 : -1;
        return 0;
      })
    : filtered;

  const selCls = "bg-white border border-slate-200 rounded-xl px-3 py-2.5 text-sm text-navy focus:border-[#F37A24] focus:ring-2 focus:ring-[#F37A24]/20 outline-none";

  return (
    <AdminLayout>
      <div className="mb-6">
        <div className="overline text-navy/65 mb-1">Platform</div>
        <h1 className="font-heading font-black text-navy text-3xl tracking-tight">Accounts</h1>
        <p className="text-navy/70 text-sm mt-1">Manage every subscriber — from solo practitioners to multi-entity firms.</p>
      </div>

      <PlatformTools admins={platformAdmins} onGrant={grantPlatform} onRevoke={revokePlatform} busy={platformBusy} />
      <div className="flex justify-end -mt-3 mb-4">
        <button onClick={openLog} data-testid="open-deletion-log"
          className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border border-slate-200 text-navy/70 hover:border-navy hover:text-navy transition-colors">
          <Warning size={13} weight="fill" className="text-navy/40" /> Deletion log
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
        <StatCard label="Total" value={counts.total ?? "—"} color="#0E0F3F" active={statusFilter === "All"} onClick={() => setStatusFilter("All")} />
        <StatCard label="Active" value={counts.active ?? "—"} color="#00A859" active={statusFilter === "active"} onClick={() => setStatusFilter("active")} />
        <StatCard label="Trialing" value={counts.trialing ?? "—"} color="#F37A24" active={statusFilter === "trialing"} onClick={() => setStatusFilter("trialing")} />
        <StatCard label="Suspended" value={counts.suspended ?? "—"} color="#E5484D" active={statusFilter === "suspended"} onClick={() => setStatusFilter("suspended")} />
        <StatCard label="Cancelled" value={counts.cancelled ?? "—"} color="#64748B" active={statusFilter === "cancelled"} onClick={() => setStatusFilter("cancelled")} />
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-navy/35" />
          <input data-testid="accounts-search" value={q}
            onChange={(e) => { setQ(e.target.value); load(e.target.value); }}
            placeholder="Search by practice or email…"
            className="w-full bg-white border border-slate-200 rounded-xl pl-9 pr-4 py-2.5 text-sm text-navy focus:border-[#F37A24] focus:ring-2 focus:ring-[#F37A24]/20 outline-none" />
        </div>
        <label className="flex items-center gap-2 text-xs text-navy/65">
          <span className="overline">Plan</span>
          <select data-testid="filter-plan" className={selCls} value={planFilter} onChange={(e) => setPlanFilter(e.target.value)}>
            <option value="All">All plans</option>
            {planNames.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-2 text-xs text-navy/65">
          <span className="overline">Orgs</span>
          <select data-testid="filter-orgs" className={selCls} value={orgsFilter} onChange={(e) => setOrgsFilter(e.target.value)}>
            {Object.keys(ORG_RANGES).map((k) => <option key={k} value={k}>{k === "All" ? "All orgs" : k}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-2 text-xs text-navy/65">
          <span className="overline">Status</span>
          <select data-testid="filter-status" className={selCls} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            {["All", "active", "trialing", "suspended", "cancelled"].map((s) => <option key={s} value={s}>{s === "All" ? "All statuses" : s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-2 text-xs text-navy/65">
          <span className="overline">Billing</span>
          <select data-testid="filter-comp" className={selCls} value={compFilter} onChange={(e) => setCompFilter(e.target.value)}>
            <option value="All">All billing</option>
            <option value="comp">Comp only</option>
            <option value="chargeable">Chargeable only</option>
          </select>
        </label>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="table-controls">
        <span className="overline text-navy/45 mr-1">Views</span>
        {[...BUILTIN_VIEWS, ...savedViews.map((v) => ({ ...v, custom: true }))].map((v) => (
          <span key={v.id} className="inline-flex items-center">
            <button onClick={() => applyView(v.filters)} data-testid={`view-${v.id}`}
              className="inline-flex items-center gap-1.5 text-xs font-semibold pl-3 pr-2.5 py-1.5 rounded-full border border-slate-200 text-navy/75 hover:border-[#F37A24] hover:text-navy transition-colors">
              <BookmarkSimple size={12} weight={v.custom ? "fill" : "regular"} className={v.custom ? "text-[#F37A24]" : "text-navy/40"} /> {v.name}
              {v.custom && (
                <span role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); deleteView(v.id); }} data-testid={`delete-view-${v.id}`}
                  className="ml-1 -mr-1 rounded-full hover:bg-slate-100 p-0.5 text-navy/40 hover:text-[#E5484D]">
                  <X size={11} weight="bold" />
                </span>
              )}
            </button>
          </span>
        ))}
        <button onClick={() => setSaveViewOpen(true)} data-testid="save-view-btn"
          className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border border-dashed border-slate-300 text-navy/60 hover:border-[#F37A24] hover:text-navy transition-colors">
          <FloppyDisk size={12} weight="bold" /> Save view
        </button>

        <div className="flex items-center gap-2 ml-auto">
          <div className="inline-flex rounded-xl border border-slate-200 overflow-hidden" data-testid="density-toggle">
            <button onClick={() => updateDensity("comfortable")} data-testid="density-comfortable"
              className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 transition-colors ${density === "comfortable" ? "bg-navy text-white" : "text-navy/60 hover:bg-slate-50"}`}>
              <Rows size={13} weight="bold" /> Comfortable
            </button>
            <button onClick={() => updateDensity("compact")} data-testid="density-compact"
              className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 transition-colors ${density === "compact" ? "bg-navy text-white" : "text-navy/60 hover:bg-slate-50"}`}>
              <ListBullets size={13} weight="bold" /> Compact
            </button>
          </div>
          <div className="relative">
            <button onClick={() => setColMenuOpen((o) => !o)} data-testid="columns-btn"
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-xl border border-slate-200 text-navy/70 hover:border-[#F37A24] hover:text-navy transition-colors">
              <SlidersHorizontal size={14} weight="bold" /> Columns
              {hiddenCols.length > 0 && <span className="ml-0.5 text-[0.6rem] font-bold px-1.5 py-0.5 rounded-full bg-[#F37A24]/15 text-[#F37A24]">{HIDEABLE_COLUMNS.length - hiddenCols.length}/{HIDEABLE_COLUMNS.length}</span>}
            </button>
            {colMenuOpen && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setColMenuOpen(false)} />
                <div className="absolute right-0 mt-2 w-56 rounded-xl border border-slate-200 bg-white shadow-xl z-40 p-2" data-testid="columns-menu">
                  <div className="overline text-navy/45 px-2 py-1.5">Show columns</div>
                  {HIDEABLE_COLUMNS.map((c) => (
                    <label key={c.key} className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-slate-50 cursor-pointer text-sm text-navy">
                      <input type="checkbox" checked={show(c.key)} onChange={() => toggleColumn(c.key)}
                        data-testid={`col-toggle-${c.key}`} className="w-4 h-4 accent-[#F37A24] cursor-pointer" />
                      {c.label}
                    </label>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {selected.size > 0 && (
        <div className="flex items-center justify-between gap-3 mb-3 rounded-xl bg-[#E5484D]/[0.06] border border-[#E5484D]/25 px-4 py-3" data-testid="cleanup-toolbar">
          <div className="text-sm text-navy">
            <b>{selected.size}</b> account{selected.size === 1 ? "" : "s"} selected for cleanup
            <button onClick={() => setSelected(new Set())} className="ml-3 text-xs text-navy/55 underline" data-testid="cleanup-clear-selection">clear</button>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setCompModal({ ids: [...selected] })} data-testid="bulk-comp-btn"
              className="inline-flex items-center gap-1.5 text-sm font-bold px-4 py-2 rounded-xl text-white" style={{ background: "#00A859" }}>
              <Gift size={15} weight="fill" /> Mark comp
            </button>
            <button onClick={openCleanup} data-testid="cleanup-review-btn"
              className="inline-flex items-center gap-1.5 text-sm font-bold px-4 py-2 rounded-xl text-white" style={{ background: "#E5484D" }}>
              <Trash size={15} weight="fill" /> Review & delete
            </button>
          </div>
        </div>
      )}

      {/* Mobile / small-screen card view — shows every field & action without horizontal scrolling */}
      <div className="xl:hidden space-y-3" data-testid="accounts-cards">
        {accounts.length === 0 && <div className="card-surface p-8 text-center text-navy/65">No accounts found.</div>}
        {accounts.map((a) => (
          <div key={a.id} className="card-surface p-4" data-testid={`account-card-${a.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-semibold text-navy flex items-center gap-2 flex-wrap">
                  <Buildings size={15} className="text-navy/35" /> {a.name}
                  {a.billing_exempt && <span className="inline-flex items-center gap-1 text-[0.6rem] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-positive/10 text-positive"><Gift size={10} weight="fill" /> Comp</span>}
                </div>
                <div className="text-xs text-navy/65 truncate">{a.owner_email}</div>
              </div>
              {a.protected ? <ShieldStar size={16} weight="fill" className="text-positive shrink-0" />
                : <input type="checkbox" checked={selected.has(a.id)} onChange={() => toggleSelect(a.id)} data-testid={`select-account-card-${a.id}`} className="w-4 h-4 accent-[#E5484D] cursor-pointer shrink-0 mt-1" />}
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 mt-3 text-sm">
              <div><div className="overline text-navy/50">Plan</div><div className="text-navy font-medium">{a.plan_name}</div></div>
              <div><div className="overline text-navy/50">Type</div><div className="text-navy">{a.status === "trialing" ? "—" : (a.billing_cycle === "annual" ? "Annual" : "Monthly")}</div></div>
              <div><div className="overline text-navy/50">Value</div><div className="text-navy font-medium tabular">{a.status === "trialing" || !a.billed_amount ? "—" : `${gbp(a.billed_amount)}/${a.billed_period}`}</div></div>
              <div><div className="overline text-navy/50">Orgs</div><div className="text-navy">{a.orgs}</div></div>
              <div>
                <div className="overline text-navy/50">Status</div>
                <span className={`text-[0.62rem] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${STATUS_STYLE[a.status] || "bg-slate-100 text-navy/70"}`} data-testid={`account-card-status-${a.id}`}>{a.status}</span>
                {a.status === "trialing" && a.trial_ends_at && <div className="text-[0.65rem] text-navy/65 mt-1">ends {new Date(a.trial_ends_at).toLocaleDateString("en-GB")}</div>}
              </div>
              <div><div className="overline text-navy/50">Lifetime value</div><div className="text-navy font-medium tabular">{a.status === "trialing" ? "—" : gbp(a.revenue_to_date)}</div></div>
              {a.status === "trialing" && a.onboarding && (
                <div className="col-span-2"><div className="overline text-navy/50">Onboarding</div>
                  <div className="text-navy text-xs">{a.onboarding.sent_count}/{a.onboarding.total} · {a.onboarding.complete ? "All sent" : (a.onboarding.last_label ? `Last: ${a.onboarding.last_label}` : "Not started")}</div></div>
              )}
            </div>
            <div className="flex flex-wrap gap-2 mt-4 pt-3 border-t border-slate-100">
              <button onClick={() => toggleComp(a)} disabled={compBusy === a.id} data-testid={`comp-toggle-card-${a.id}`}
                className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border transition-colors disabled:opacity-60 ${a.billing_exempt ? "border-positive/40 text-positive bg-positive/5" : "border-slate-200 text-navy hover:border-[#F37A24]"}`}>
                <Gift size={13} weight="fill" /> {a.billing_exempt ? "Comp on" : "Comp"}
              </button>
              <button onClick={() => setTransferFor(a)} data-testid={`transfer-card-${a.id}`} className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border border-slate-200 text-navy hover:border-[#F37A24] transition-colors"><PaperPlaneTilt size={13} weight="bold" /> Transfer</button>
              <button onClick={() => setPricingFor(a)} data-testid={`set-pricing-card-${a.id}`} className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border border-slate-200 text-navy hover:border-[#F37A24] transition-colors"><CurrencyGbp size={13} weight="bold" /> Pricing</button>
              {a.status === "trialing" && (
                <button onClick={() => setExtendFor(a)} data-testid={`extend-trial-card-${a.id}`} className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border border-slate-200 text-navy hover:border-[#F37A24] transition-colors"><Clock size={13} weight="bold" /> Extend</button>
              )}
              {a.status === "trialing" && a.onboarding && !a.onboarding.complete && (
                <button onClick={() => sendOnboarding(a)} disabled={obBusy === a.id} data-testid={`send-onboarding-card-${a.id}`} className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border border-slate-200 text-navy hover:border-[#F37A24] transition-colors disabled:opacity-50"><PaperPlaneTilt size={13} weight="fill" /> {obBusy === a.id ? "…" : "Send"}</button>
              )}
              {a.status === "suspended" || a.status === "cancelled" ? (
                <button onClick={() => setStatus(a, "active")} disabled={busy === a.id} data-testid={`unsuspend-card-${a.id}`} className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg text-white disabled:opacity-60" style={{ background: "#00A859" }}><CheckCircle size={13} weight="fill" /> Reactivate</button>
              ) : (
                <button onClick={() => setStatus(a, "suspended")} disabled={busy === a.id} data-testid={`suspend-card-${a.id}`} className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg text-white disabled:opacity-60" style={{ background: "#E5484D" }}><Prohibit size={13} weight="fill" /> Suspend</button>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="card-surface overflow-x-auto hidden xl:block">
        <style>{`.acc-compact td{padding-top:.35rem;padding-bottom:.35rem}.acc-compact th{padding-top:.4rem;padding-bottom:.4rem}`}</style>
        <table className={`w-full text-sm min-w-[1180px] ${density === "compact" ? "acc-compact" : ""}`} data-testid="accounts-table">
          <thead>
            <tr className="text-left text-navy/65 border-b border-slate-100">
              <th className="pl-5 pr-1 py-3 sticky left-0 z-20 bg-white w-[52px]"></th>
              <SortTh label="Practice" sortKey="name" sort={sort} onSort={toggleSort} className="sticky left-[52px] z-20 bg-white min-w-[220px] border-r border-slate-100" />
              {show("plan") && <SortTh label="Plan" sortKey="plan" sort={sort} onSort={toggleSort} />}
              {show("cycle") && <SortTh label="Type" sortKey="cycle" sort={sort} onSort={toggleSort} />}
              {show("value") && <SortTh label="Value" sortKey="value" sort={sort} onSort={toggleSort} align="right" />}
              {show("orgs") && <SortTh label="Orgs" sortKey="orgs" sort={sort} onSort={toggleSort} />}
              {show("status") && <SortTh label="Status" sortKey="status" sort={sort} onSort={toggleSort} />}
              {show("setup") && <th className="px-3 py-3 font-semibold overline">Setup</th>}
              {show("onboarding") && <th className="px-3 py-3 font-semibold overline">Onboarding</th>}
              {show("ltv") && <SortTh label="Lifetime value" sortKey="ltv" sort={sort} onSort={toggleSort} align="right" />}
              <th className="px-5 py-3 font-semibold overline text-right sticky right-0 z-20 bg-white border-l border-slate-100">Actions</th>
            </tr>
          </thead>
          <tbody>
            {accounts.map((a) => (
              <tr key={a.id} className="group border-b border-slate-50 last:border-0 hover:bg-slate-50/50" data-testid={`account-row-${a.id}`}>
                <td className="pl-5 pr-1 py-3.5 sticky left-0 z-10 bg-white group-hover:bg-slate-50/50 w-[52px]">
                  {a.protected ? (
                    <span title="Protected — cannot be deleted (comp account, live subscription, or platform admin)" data-testid={`account-protected-${a.id}`}>
                      <ShieldStar size={16} weight="fill" className="text-positive" />
                    </span>
                  ) : (
                    <input type="checkbox" checked={selected.has(a.id)} onChange={() => toggleSelect(a.id)}
                      data-testid={`select-account-${a.id}`} className="w-4 h-4 accent-[#E5484D] cursor-pointer" />
                  )}
                </td>
                <td className="px-5 py-3.5 pl-3 sticky left-[52px] z-10 bg-white group-hover:bg-slate-50/50 min-w-[220px] border-r border-slate-100">
                  <div className="font-semibold text-navy flex items-center gap-2">
                    <Buildings size={15} className="text-navy/35" /> {a.name}
                    {a.billing_exempt && <span title={a.comp_reason ? `Comp: ${a.comp_reason}${a.comp_until ? ` · reverts ${a.comp_until.slice(0,10)}` : ""}` : "Comp account"} className="inline-flex items-center gap-1 text-[0.6rem] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-positive/10 text-positive" data-testid={`comp-badge-${a.id}`}><Gift size={10} weight="fill" /> Comp{a.comp_reason ? `· ${a.comp_reason.split(" — ")[0]}` : ""}{a.comp_until ? ` · →${a.comp_until.slice(5,10)}` : ""}</span>}
                  </div>
                  <div className="text-xs text-navy/65">{a.owner_email}</div>
                </td>
                {show("plan") && (
                <td className="px-3 py-3.5">
                  <div className="text-navy font-medium">{a.plan_name}</div>
                  {a.custom_enterprise && <div className="text-xs text-navy/65">{gbp(a.custom_enterprise.monthly)}/mo custom</div>}
                </td>
                )}
                {show("cycle") && (
                <td className="px-3 py-3.5" data-testid={`account-cycle-${a.id}`}>
                  {a.status === "trialing" ? (
                    <span className="text-navy/30 text-xs">—</span>
                  ) : (
                    <span className={`text-[0.62rem] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${CYCLE_STYLE[a.billing_cycle === "annual" ? "annual" : "monthly"]}`}>
                      {a.billing_cycle === "annual" ? "Annual" : "Monthly"}
                    </span>
                  )}
                </td>
                )}
                {show("value") && (
                <td className="px-3 py-3.5 text-right" data-testid={`account-value-${a.id}`}>
                  {a.status === "trialing" || !a.billed_amount ? (
                    <span className="text-navy/30 text-xs">—</span>
                  ) : (
                    <div>
                      <div className="font-semibold text-navy tabular">{gbp(a.billed_amount)}<span className="text-navy/65 font-normal">/{a.billed_period}</span></div>
                      {a.billed_period === "yr" && <div className="text-[0.65rem] text-navy/65 tabular">≈ {gbp(a.mrr)}/mo</div>}
                    </div>
                  )}
                </td>
                )}
                {show("orgs") && <td className="px-3 py-3.5 text-navy">{a.orgs}</td>}
                {show("status") && (
                <td className="px-3 py-3.5">
                  <span className={`text-[0.62rem] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${STATUS_STYLE[a.status] || "bg-slate-100 text-navy/70"}`} data-testid={`account-status-${a.id}`}>
                    {a.status}
                  </span>
                  {a.status === "trialing" && a.trial_ends_at && (
                    <div className="text-[0.65rem] text-navy/65 mt-1">
                      ends {new Date(a.trial_ends_at).toLocaleDateString("en-GB")}
                    </div>
                  )}
                </td>
                )}
                {show("setup") && (
                <td className="px-3 py-3.5" data-testid={`setup-cell-${a.id}`}>
                  {a.status === "trialing" ? <SetupRing setup={a.setup} /> : <span className="text-navy/30 text-xs">—</span>}
                </td>
                )}
                {show("onboarding") && (
                <td className="px-3 py-3.5" data-testid={`onboarding-cell-${a.id}`}>
                  {a.status === "trialing" && a.onboarding ? (
                    <div className="flex items-center gap-2.5">
                      <div>
                        <div className="text-xs font-semibold text-navy tabular" data-testid={`onboarding-progress-${a.id}`}>
                          {a.onboarding.sent_count}/{a.onboarding.total}
                        </div>
                        <div className="text-[0.65rem] text-navy/65">
                          {a.onboarding.complete
                            ? "All sent"
                            : a.onboarding.last_label
                              ? `Last: ${a.onboarding.last_label}`
                              : "Not started"}
                        </div>
                        {a.onboarding.state && (
                          <div className="text-[0.6rem] text-navy/35" data-testid={`onboarding-state-${a.id}`}>
                            {OB_STATE_LABEL[a.onboarding.state] || a.onboarding.state}
                          </div>
                        )}
                      </div>
                      {!a.onboarding.complete && (
                        <button onClick={() => sendOnboarding(a)} disabled={obBusy === a.id}
                          data-testid={`send-onboarding-${a.id}`} title={`Send next: ${a.onboarding.next_label}`}
                          className="inline-flex items-center gap-1 text-[0.65rem] font-semibold px-2 py-1 rounded-lg border border-slate-200 text-navy hover:border-[#F37A24] transition-colors disabled:opacity-50">
                          <PaperPlaneTilt size={12} weight="fill" /> {obBusy === a.id ? "…" : "Send"}
                        </button>
                      )}
                    </div>
                  ) : (
                    <span className="text-navy/30 text-xs">—</span>
                  )}
                </td>
                )}
                {show("ltv") && (
                <td className="px-3 py-3.5 text-right" data-testid={`account-ltv-${a.id}`}>
                  {a.status === "trialing" ? (
                    <span className="text-navy/30 text-xs">—</span>
                  ) : (
                    <div>
                      <div className="font-semibold text-navy tabular">{gbp(a.revenue_to_date)}</div>
                      <div className="text-[0.65rem] text-navy/65 tabular">
                        LTV {a.projected_ltv != null ? gbp(a.projected_ltv) : "—"}
                      </div>
                    </div>
                  )}
                </td>
                )}
                <td className="px-5 py-3.5 sticky right-0 z-10 bg-white group-hover:bg-slate-50/50 border-l border-slate-100">
                  <div className="flex items-center justify-end gap-2">
                    <button onClick={() => toggleComp(a)} disabled={compBusy === a.id} data-testid={`comp-toggle-${a.id}`}
                      title={a.billing_exempt ? "Make chargeable again" : "Make this a comp (non-chargeable) account"}
                      className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border transition-colors disabled:opacity-60 ${a.billing_exempt ? "border-positive/40 text-positive bg-positive/5" : "border-slate-200 text-navy hover:border-[#F37A24]"}`}>
                      <Gift size={13} weight="fill" /> {a.billing_exempt ? "Comp on" : "Comp"}
                    </button>
                    <button onClick={() => setTransferFor(a)} data-testid={`transfer-${a.id}`}
                      title="Reassign this account to another user"
                      className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border border-slate-200 text-navy hover:border-[#F37A24] transition-colors">
                      <PaperPlaneTilt size={13} weight="bold" /> Transfer
                    </button>
                    <button onClick={() => setPricingFor(a)} data-testid={`set-pricing-${a.id}`}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border border-slate-200 text-navy hover:border-[#F37A24] transition-colors">
                      <CurrencyGbp size={13} weight="bold" /> Pricing
                    </button>
                    {a.status === "trialing" && (
                      <button onClick={() => setExtendFor(a)} data-testid={`extend-trial-${a.id}`}
                        title="Extend this client's trial by a number of days"
                        className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border border-slate-200 text-navy hover:border-[#F37A24] transition-colors">
                        <Clock size={13} weight="bold" /> Extend
                      </button>
                    )}
                    {a.status === "suspended" || a.status === "cancelled" ? (
                      <button onClick={() => setStatus(a, "active")} disabled={busy === a.id} data-testid={`unsuspend-${a.id}`}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg text-white transition-transform hover:scale-[1.03] disabled:opacity-60" style={{ background: "#00A859" }}>
                        <CheckCircle size={13} weight="fill" /> Reactivate
                      </button>
                    ) : (
                      <>
                        <button onClick={() => setStatus(a, "suspended")} disabled={busy === a.id} data-testid={`suspend-${a.id}`}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg text-white transition-transform hover:scale-[1.03] disabled:opacity-60" style={{ background: "#E5484D" }}>
                          <Prohibit size={13} weight="fill" /> Suspend
                        </button>
                        <button onClick={() => setStatus(a, "cancelled")} disabled={busy === a.id} data-testid={`cancel-${a.id}`}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border border-slate-300 text-navy/70 hover:border-navy hover:text-navy transition-colors disabled:opacity-60">
                          <X size={13} weight="bold" /> Cancel
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {accounts.length === 0 && (
              <tr><td colSpan={3 + HIDEABLE_COLUMNS.filter((c) => show(c.key)).length} className="px-5 py-10 text-center text-navy/65">No accounts found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {pricingFor && <PricingModal account={pricingFor} plans={plans} onClose={() => setPricingFor(null)} onSaved={() => { setPricingFor(null); load(q); }} />}
      {cleanupItems && <CleanupModal items={cleanupItems} deleting={deleting} onClose={() => setCleanupItems(null)} onConfirm={confirmCleanup} />}
      {transferFor && <TransferModal account={transferFor} busy={transferBusy} onClose={() => setTransferFor(null)} onConfirm={doTransfer} />}
      {extendFor && <ExtendTrialModal account={extendFor} busy={extendBusy} onClose={() => setExtendFor(null)} onConfirm={doExtendTrial} />}
      {compModal && <CompReasonModal count={compModal.ids.length} busy={compBusy === "modal"} onClose={() => setCompModal(null)} onConfirm={applyComp} />}
      {logOpen && <DeletionLogModal entries={logEntries} onClose={() => setLogOpen(false)} onRestore={restoreAccount} />}
      {saveViewOpen && <SaveViewModal onClose={() => setSaveViewOpen(false)} onSave={saveCurrentView} />}
    </AdminLayout>
  );
}
